import {
  DrawdownTargetPercentagesWithSubAssetClassesDto,
  PacingTabAutoPlanWizardModalPageGroupQuery,
  PacingTabAutoPlanWizardModalPageLegalEntityQuery,
  StartDateOptions
} from '@/__generated__/graphql';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { updateWizardSteps } from '@/components/basicComponents/wizard/progressBar';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import {
  EDIT_INVESTOR_PAGE_DEFAULT_DRAWDOWN_TARGETS_QUERY,
  UPDATE_LEGAL_ENTITY_MUTATION
} from '@/components/investors-page/editInvestorPage/queries';
import { InvestorDetailsEntityTypeEnum, InvestorDetailsTabsEnum } from '@/components/investors-page/investorDetailsPage/types';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { AssetClassWithPrevValues } from '@/components/settings-page/assetClassDefaultTargets';
import { AssetClassTargetsModalPage } from '@/components/settings-page/assetClassDefaultTargets/assetClassTargetsModalPage';
import { GET_ASSET_CLASSES_QUERY } from '@/components/settings-page/assetClassDefaultTargets/queries';
import {
  combineTargets,
  getAllocationTargetsToUpdate,
  getSumAssetClassPercentages,
  resetAssetClassTargets,
  structurAssetClassesWithPrevValues,
  updateAssetClassDrawdownTargets
} from '@/components/settings-page/assetClassDefaultTargets/utils';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useStatusData } from '@/hooks/useStatusData';
import { getUTCDate } from '@/shared/utils/date';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { AUTO_PLAN_MUTATION } from '../../queries';
import { DrawdownTargetsDto } from '../../types';
import { PACING_TAB_AUTO_PLAN_WIZARD_MODAL_PAGE_GROUP_QUERY, PACING_TAB_AUTO_PLAN_WIZARD_MODAL_PAGE_LEGAL_ENTITY_QUERY } from './queries';
import { AllocationTargets } from './steps/allocationTargets';
import { AutoPlan } from './steps/autoPlan';
import { ForecastOptions } from './steps/forecastOptions';
import { PlanDetails } from './steps/planDetails';

const autoPlanWizardStepsInitial = [
  { name: 'Plan Details', status: 'IN_PROGRESS' },
  { name: 'Allocation Targets', status: 'NOT_STARTED' },
  { name: 'Forecast Options', status: 'NOT_STARTED' },
  { name: 'Auto Plan', status: 'NOT_STARTED' }
];

export interface ICreateAutoPlanForm {
  name: string;
  startDate: 'Today' | 'Next Quarter';
  yearsToAchieveAllocationTarget: string;
  biteSize: string;
  growthRate: string;
  totalEntityValue: string;
  privatePercentage: string;
  generateAllocations: boolean;
}

const defaultFormValues: ICreateAutoPlanForm = {
  name: '',
  startDate: 'Today',
  yearsToAchieveAllocationTarget: '5',
  biteSize: '',
  growthRate: '',
  totalEntityValue: '',
  privatePercentage: '',
  generateAllocations: true
};

const getDefaultPlanStart = (): 'Today' | 'Next Quarter' => {
  const today = new Date();
  const month = today.getMonth();
  const year = today.getFullYear();

  const quarterStartMonth = Math.floor(month / 3) * 3;
  const quarterEndMonth = quarterStartMonth + 2;

  const quarterStartDate = new Date(year, quarterStartMonth, 1);
  const quarterEndDate = new Date(year, quarterEndMonth + 1, 0);

  const quarterMidDate = new Date(quarterStartDate.getTime() + (quarterEndDate.getTime() - quarterStartDate.getTime()) / 2);

  if (today <= quarterMidDate) {
    return 'Today';
  }
  return 'Next Quarter';
};

const startDateValues = {
  Today: StartDateOptions.Today,
  'Next Quarter': StartDateOptions.NextQuarter
};

const assetClassOptions = ['Use default asset class allocation targets', 'Customize targets for this investor entity'];

interface AutoPlanWizardModalPageProps {
  entityType: InvestorDetailsEntityTypeEnum;
  backToTitle: string;
  handleAutoPlanWizardPage: ({ open }: { open: boolean; withRefetch?: boolean }) => void;
}

const AutoPlanWizardModalPage = ({ entityType, backToTitle, handleAutoPlanWizardPage }: AutoPlanWizardModalPageProps) => {
  const { handleSubmit, control, reset, trigger, setValue } = useForm<ICreateAutoPlanForm>({ defaultValues: defaultFormValues });
  const params = useParams();
  const navigate = useNavigate();
  const { data: statusData } = useStatusData();
  const { searchParams, setSearchParams } = useSearchParamsManager();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';

  const isGroup = searchParams.get('isGroup') === 'true';
  const openAutoPlanWizard = searchParams.get('openAutoPlanWizard') || '';

  const [autoPlanWizardSteps, setAutoPlanWizardSteps] = useState(autoPlanWizardStepsInitial);
  const [assetClassTargets, setAssetClassTargets] = useState<AssetClassWithPrevValues[]>([]);
  const [assetClassChartWithTableData, setAssetClassChartWithTableData] = useState<DrawdownTargetsDto | null>(null);
  const [isAssetClassTargetsPageOpen, setIsAssetClassTargetsPageOpen] = useState<boolean>(false);
  const [assetClassSelectedOption, setAssetClassSelectedOption] = useState(assetClassOptions[0]);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [getAssetClasses] = useLazyQuery(GET_ASSET_CLASSES_QUERY);
  const [getDefaultDrawdownTargets] = useLazyQuery(EDIT_INVESTOR_PAGE_DEFAULT_DRAWDOWN_TARGETS_QUERY);

  const [createAutoPlan, { loading: createAutoPlanLoading }] = useMutation(AUTO_PLAN_MUTATION, {
    onError: (error) => {
      console.error(error);
    }
  });

  const [updateEntity, { loading: updateEntityLoading }] = useMutation(UPDATE_LEGAL_ENTITY_MUTATION, {
    onError: (error) => {
      console.error(error);
    }
  });

  const { data: legalEntityData, loading: legalEntityDataLoading } = useQuery(PACING_TAB_AUTO_PLAN_WIZARD_MODAL_PAGE_LEGAL_ENTITY_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    skip: entityType === InvestorDetailsEntityTypeEnum.Group,
    variables: {
      id: entityId
    }
  });

  const { data: groupData, loading: groupDataLoading } = useQuery(PACING_TAB_AUTO_PLAN_WIZARD_MODAL_PAGE_GROUP_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    skip: entityType !== InvestorDetailsEntityTypeEnum.Group,
    variables: {
      data: {
        primaryEntityId: entityId
      }
    }
  });

  const initializeAssetClassTargets = async ({
    legalEntityData,
    groupData
  }: {
    legalEntityData?: PacingTabAutoPlanWizardModalPageLegalEntityQuery['legalEntity'];
    groupData?: PacingTabAutoPlanWizardModalPageGroupQuery['group'];
  }) => {
    try {
      const { tenant, allocationTargets, drawdownTargets } = legalEntityData || groupData || {};

      const { data: assetClassesData } = await getAssetClasses({
        variables: {
          tenantId: tenant?.id
        }
      });

      if (!allocationTargets.length) {
        const { data: defaultDrawdownTargetsData } = await getDefaultDrawdownTargets({
          variables: {
            tenantId: tenant?.id
          }
        });

        const combinedDefaultTargets = combineTargets([
          ...defaultDrawdownTargetsData.defaultDrawdownTargets.targets.drawdown,
          ...defaultDrawdownTargetsData.defaultDrawdownTargets.targets.evergreen
        ]);

        const targets = structurAssetClassesWithPrevValues(combinedDefaultTargets, assetClassesData.assetClasses);
        setAssetClassTargets(targets);
        const sumAssetClassPercentages = getSumAssetClassPercentages(targets);
        setValue('privatePercentage', sumAssetClassPercentages);
        setAssetClassChartWithTableData(defaultDrawdownTargetsData?.defaultDrawdownTargets || null);
        setAssetClassSelectedOption(assetClassOptions[0]);
        return;
      }

      const combinedTargets = combineTargets([...drawdownTargets.targets.drawdown, ...drawdownTargets.targets.evergreen]);

      const targets = structurAssetClassesWithPrevValues(combinedTargets, assetClassesData.assetClasses);
      setAssetClassTargets(targets);
      const sumAssetClassPercentages = getSumAssetClassPercentages(targets);
      setValue('privatePercentage', sumAssetClassPercentages);
      setAssetClassChartWithTableData(drawdownTargets);
      setAssetClassSelectedOption(assetClassOptions[1]);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (groupDataLoading || legalEntityDataLoading) return;

    if (legalEntityData?.legalEntity) {
      const { entityName, totalEntityValue } = legalEntityData.legalEntity || {};
      reset({
        ...defaultFormValues,
        name: `Plan for ${entityName ?? ''} ${getUTCDate(new Date()).toUpperCase()}`,
        startDate: getDefaultPlanStart(),
        totalEntityValue: totalEntityValue?.toString() ?? ''
      });
      initializeAssetClassTargets({ legalEntityData: legalEntityData.legalEntity });
      return;
    }

    if (groupData?.group) {
      const { name, totalEntityValue } = groupData.group || {};
      reset({
        ...defaultFormValues,
        name: `Plan for ${name ?? ''} ${getUTCDate(new Date()).toUpperCase()}`,
        startDate: getDefaultPlanStart(),
        totalEntityValue: totalEntityValue?.toString() ?? ''
      });
      initializeAssetClassTargets({ groupData: groupData.group });
      return;
    }
  }, [groupDataLoading, groupData, legalEntityDataLoading, legalEntityData]);

  const handleCloseAutoPlanWizardModalPage = () => {
    handleAutoPlanWizardPage({ open: false });

    if (openAutoPlanWizard === 'true') {
      setSearchParams(
        (prev) => {
          prev.set('activeTab', InvestorDetailsTabsEnum.SUMMARY);
          prev.delete('openAutoPlanWizard');
          return prev;
        },
        { replace: true }
      );
    }
  };

  const nextStep = () => {
    const currentStepIndex = autoPlanWizardSteps.findIndex((step) => step.status === 'IN_PROGRESS');
    if (currentStepIndex === autoPlanWizardSteps.length - 1) return;

    const updatedAutoPlanWizardSteps = autoPlanWizardSteps.map((step, index) => {
      if (currentStepIndex === index) {
        return { ...step, status: 'COMPLETED' };
      }
      if (currentStepIndex + 1 === index) {
        return { ...step, status: 'IN_PROGRESS' };
      }

      return step;
    });

    setAutoPlanWizardSteps(updatedAutoPlanWizardSteps);
  };

  const prevStep = () => {
    updateWizardSteps('prev', autoPlanWizardSteps, setAutoPlanWizardSteps);
  };

  const handleGoBack = () => {
    if (autoPlanWizardSteps[0].status === 'IN_PROGRESS') {
      handleCloseAutoPlanWizardModalPage();
      return;
    }
    prevStep();
  };

  const openAssetClassTargetsPage = () => {
    setIsAssetClassTargetsPageOpen(true);
    setScrollPosition(window.scrollY);
  };

  const closeAssetClassTargetsPage = () => {
    setIsAssetClassTargetsPageOpen(false);
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
    setScrollPosition(0);
  };

  const resetAssetClassTargetsToDefaultValues = (assetClassTargets: AssetClassWithPrevValues[]) => {
    const defaultPrivatePercentage = assetClassTargets
      .reduce((acc, assetClass) => {
        return acc + parseFloat(assetClass?.defaultPercentage ?? '0');
      }, 0)
      .toFixed(2);

    const defaultAssetClassTargets = resetAssetClassTargets(assetClassTargets);
    const updatedAssetClassDrawdownTargets: DrawdownTargetPercentagesWithSubAssetClassesDto[] = updateAssetClassDrawdownTargets(
      assetClassChartWithTableData.targets.drawdown,
      defaultAssetClassTargets,
      defaultPrivatePercentage
    );
    const updatedAssetClassEvergreen: DrawdownTargetPercentagesWithSubAssetClassesDto[] = updateAssetClassDrawdownTargets(
      assetClassChartWithTableData.targets.evergreen,
      defaultAssetClassTargets,
      defaultPrivatePercentage
    );

    setValue('privatePercentage', defaultPrivatePercentage);
    setAssetClassChartWithTableData({
      ...assetClassChartWithTableData,
      targets: { drawdown: updatedAssetClassDrawdownTargets, evergreen: updatedAssetClassEvergreen }
    });
    setAssetClassTargets(defaultAssetClassTargets);
    closeAssetClassTargetsPage();
  };

  const saveAssetClassTargets = (assetClassTargets: AssetClassWithPrevValues[], privatePercentage: string) => {
    const updatedAssetClassDrawdownTargets = updateAssetClassDrawdownTargets(
      assetClassChartWithTableData.targets.drawdown,
      assetClassTargets,
      privatePercentage
    );
    const updatedAssetClassEvergreen = updateAssetClassDrawdownTargets(
      assetClassChartWithTableData.targets.evergreen,
      assetClassTargets,
      privatePercentage
    );

    setValue('privatePercentage', privatePercentage);
    setAssetClassChartWithTableData({
      ...assetClassChartWithTableData,
      targets: { drawdown: updatedAssetClassDrawdownTargets, evergreen: updatedAssetClassEvergreen }
    });
    const updatedAssetClassTargets = assetClassTargets.map((assetClass) => {
      return {
        ...assetClass,
        prevPercentage: assetClass.percentage,
        subAssetClasses: assetClass.subAssetClasses.map((subAssetClass) => {
          return {
            ...subAssetClass,
            prevPercentage: subAssetClass.percentage
          };
        })
      };
    });
    setAssetClassTargets(updatedAssetClassTargets);
    closeAssetClassTargetsPage();
  };

  const onSave = async (data: ICreateAutoPlanForm) => {
    setIsSubmitting(true);
    const { name, growthRate, yearsToAchieveAllocationTarget, startDate, generateAllocations, totalEntityValue, privatePercentage } = data;
    const allocationTargets = getAllocationTargetsToUpdate(assetClassTargets, statusData?.target_sub_asset_classes);

    try {
      await updateEntity({
        variables: {
          data: {
            id: entityId,
            ...(entityType !== InvestorDetailsEntityTypeEnum.Group && {
              totalEntityValue: parseFloat(totalEntityValue)
            }),
            allocationTargets: assetClassSelectedOption === assetClassOptions[0] ? [] : allocationTargets,
            privatePercentage: parseFloat(privatePercentage),
            ignoreAllocationTargets: false
          }
        }
      });

      const { data: createAutoPlanData } = await createAutoPlan({
        variables: {
          input: {
            legalEntityId: entityId,
            name: name.trim(),
            startDate: startDateValues[startDate],
            growthRate: parseFloat(growthRate),
            yearsToAchieveAllocationTarget: parseInt(yearsToAchieveAllocationTarget),
            generateAllocations: generateAllocations
          }
        }
      });

      const basePath = `/investors/${investorId}/${entityId}`;
      if (generateAllocations && createAutoPlanData?.autoPlan?.id) {
        if (backToTitle === 'All Commitment Plans') {
          navigate(
            `${basePath}?activeTab=Pacing&commitmentPlanId=${createAutoPlanData.autoPlan.id}&backToTitle=All Commitment Plans&isGroup=${isGroup}`
          );
          return;
        }
        setSearchParams(
          (prev) => {
            prev.set('commitmentPlanId', createAutoPlanData.autoPlan.id);
            prev.set('activeTab', InvestorDetailsTabsEnum.PACING);
            if (openAutoPlanWizard === 'true') {
              prev.delete('openAutoPlanWizard');
            }
            return prev;
          },
          { replace: true }
        );
        handleAutoPlanWizardPage({ open: false, withRefetch: true });
        return;
      }

      navigate(`${basePath}/editCommitmentPlan?backToTitle=${backToTitle}&isGroup=${isGroup}`, {
        state: { autoPlanData: JSON.stringify(createAutoPlanData.autoPlan) }
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (legalEntityDataLoading || groupDataLoading) {
    return <LoaderOnWholeScreen />;
  }

  if (isAssetClassTargetsPageOpen) {
    return (
      <AssetClassTargetsModalPage
        assetClassTargets={assetClassTargets}
        targetSubAssetClasses={statusData?.target_sub_asset_classes}
        saveAssetClassTargets={saveAssetClassTargets}
        resetAssetClassTargetsToDefaultValues={resetAssetClassTargetsToDefaultValues}
        closeAssetClassTargetsPage={closeAssetClassTargetsPage}
        assetClassSelectedOption={assetClassSelectedOption}
        setAssetClassSelectedOption={setAssetClassSelectedOption}
      />
    );
  }

  return (
    <>
      {(isSubmitting || createAutoPlanLoading || updateEntityLoading) && (
        <ModalWrappedContent
          isOpen={true}
          onClose={() => {}}
          title="Please Wait"
          showCloseIcon={false}
          showCancelButton={false}
          isUseOutsideClick={false}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header
          modalControl={<GoBackButton handleClose={handleGoBack} backToTitle={autoPlanWizardSteps[0].status === 'IN_PROGRESS' ? backToTitle : ''} />}
        />
        <PageTitle title="Auto Plan" />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          {autoPlanWizardSteps[0].status === 'IN_PROGRESS' && (
            <PlanDetails
              autoPlanWizardSteps={autoPlanWizardSteps}
              control={control}
              trigger={trigger}
              startOver={handleCloseAutoPlanWizardModalPage}
              nextStep={nextStep}
            />
          )}
          {autoPlanWizardSteps[1].status === 'IN_PROGRESS' && (
            <AllocationTargets
              autoPlanWizardSteps={autoPlanWizardSteps}
              control={control}
              entityType={entityType}
              assetClassChartWithTableData={assetClassChartWithTableData}
              trigger={trigger}
              prevStep={prevStep}
              nextStep={nextStep}
              openAssetClassTargetsPage={openAssetClassTargetsPage}
            />
          )}
          {autoPlanWizardSteps[2].status === 'IN_PROGRESS' && (
            <ForecastOptions autoPlanWizardSteps={autoPlanWizardSteps} control={control} trigger={trigger} prevStep={prevStep} nextStep={nextStep} />
          )}
          {autoPlanWizardSteps[3].status === 'IN_PROGRESS' && (
            <AutoPlan
              autoPlanWizardSteps={autoPlanWizardSteps}
              control={control}
              isSubmitting={isSubmitting}
              prevStep={prevStep}
              nextStep={handleSubmit(onSave)}
            />
          )}
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

export default AutoPlanWizardModalPage;
